import React, { useState } from 'react';
import { Monitor, Smartphone, Tablet, Tv, Laptop, X, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { api } from '@/api';

const deviceIcons = {
  'iPhone': Smartphone,
  'iPad': Tablet,
  'Android Phone': Smartphone,
  'Android Tablet': Tablet,
  'Mac': Laptop,
  'Windows PC': Monitor,
  'Linux PC': Monitor,
  'Smart TV': Tv,
  'Roku': Tv,
  'Unknown Device': Monitor,
};

export const DeviceLimitModal = ({ sessions, maxDevices, onSessionRevoked, onClose }) => {
  const [revokingId, setRevokingId] = useState(null);

  const handleRevoke = async (sessionId) => {
    setRevokingId(sessionId);
    try {
      await api.revokeSession(sessionId);
      onSessionRevoked(sessionId);
    } catch {
      alert('Failed to remove device. Please try again.');
    } finally {
      setRevokingId(null);
    }
  };

  const formatTime = (isoStr) => {
    if (!isoStr) return '';
    const d = new Date(isoStr);
    const now = new Date();
    const diffMin = Math.round((now - d) / 60000);
    if (diffMin < 1) return 'Just now';
    if (diffMin < 60) return `${diffMin}m ago`;
    const diffHrs = Math.round(diffMin / 60);
    if (diffHrs < 24) return `${diffHrs}h ago`;
    return d.toLocaleDateString();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm" data-testid="device-limit-modal">
      <div className="bg-gray-900 border border-gray-700 rounded-2xl p-6 max-w-md w-full mx-4 shadow-2xl">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-white">Device Limit Reached</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>

        <p className="text-gray-400 text-sm mb-6">
          You're streaming on {sessions.length} device{sessions.length > 1 ? 's' : ''}. 
          Your plan allows {maxDevices} concurrent streams. Remove a device to continue watching here.
        </p>

        <div className="space-y-3 mb-6">
          {sessions.map((session) => {
            const DeviceIcon = deviceIcons[session.device_name] || Monitor;
            return (
              <div 
                key={session.id} 
                className="flex items-center justify-between bg-gray-800 rounded-xl p-3 border border-gray-700"
                data-testid={`device-session-${session.id}`}
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-sky-500/10 rounded-lg flex items-center justify-center">
                    <DeviceIcon className="w-5 h-5 text-sky-400" />
                  </div>
                  <div>
                    <p className="text-white text-sm font-medium">{session.device_name}</p>
                    <p className="text-gray-500 text-xs">
                      {session.ip_address} · Active {formatTime(session.last_active)}
                    </p>
                  </div>
                </div>
                <Button
                  size="sm"
                  variant="destructive"
                  onClick={() => handleRevoke(session.id)}
                  disabled={revokingId === session.id}
                  data-testid={`revoke-session-${session.id}`}
                >
                  {revokingId === session.id ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    'Remove'
                  )}
                </Button>
              </div>
            );
          })}
        </div>

        <p className="text-gray-500 text-xs text-center">
          Sessions automatically expire after 2 hours of inactivity.
        </p>
      </div>
    </div>
  );
};
